import React from "react";

import { HStack, VStack } from "@UI/Components/Stack/Stack";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Box } from "@UI/Components/Box/Box";
import { Image, AspectRatio } from "@UI/Components/Image/Image";
import { Link } from "@UI/Components/Link/Link";
import { Icon } from "@UI/Components/Icon/Icon";
import { Divider } from "@UI/Components/Divider/Divider";
import { Grid, GridItem } from "@UI/Components/Grid/Grid";
import { DetailLine } from "@UI/Components/DetailLine/DetailLine";
import { LocationDot } from "@UI/Components/Icons/LocationDot";
import { ChevronDown } from "@UI/Components/Icons/ChevronDown";
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
} from "@UI/Components/Accordion/Accordion";
import { Menu, MenuButton, MenuItem, MenuList } from "@UI/Components/Menu/Menu";
import { IconButton } from "@UI/Components/Button/IconButton";
import { Cog } from "@UI/Components/Icons/Cog";
import { useServiceRequestFlow } from "../../hooks/useServiceRequestFlow";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "../messages";
import { useDisclosure } from "@UI/hooks/useDisclosure";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useServiceRequestCandidates } from "@modules/ServiceRequest/hooks/useCandidates";
import { ConfirmationModal } from "@Shared/Components/ConfirmationModal/ConfirmationModal";
import { Tooltip } from "@UI/Components/Tooltip/Tooltip";
import useI18n from "@I18n/hooks/useI18n";
import { dateFromNow } from "@utils/dateFromNow";

// SP-initiated tasks don't carry the answers-based `serviceRequest.answers`
// data the new task-creation flow produces, so this mirrors
// ServiceRequestDetailCardAiAssisted's DetailLine/accordion structure while
// sourcing content from the legacy fields ExpandedServiceRequestDetails used
// (pictures, proOnly, postedDate) instead.
export const ServiceRequestDetailCardSpInitiated = () => {
  const { serviceRequest } = useServiceRequestFlow();
  const { formatMessage } = useI18n();
  const isPostedForClient =
    !!serviceRequest?.authorId &&
    serviceRequest.authorId !== serviceRequest.userId;
  const { isOpen, onToggle } = useDisclosure();
  const {
    isOpen: isCancelConfirmationOpen,
    onOpen: onCancelConfirmationOpen,
    onClose: onCancelConfirmationClose,
  } = useDisclosure();
  const navigate = useNavigate();

  const { candidates = [] } = useServiceRequestCandidates({
    serviceRequestId: serviceRequest?.id ?? "",
    sortBy: "recommended",
  });

  const pictures = serviceRequest?.pictures || [];

  // The flow starts with no service request and fetches it, so every read
  // below this line would be a read off `undefined` on the first render. The
  // guard sits after the last hook, since a hook may not be skipped.
  if (!serviceRequest) return null;

  const serviceRequestHasOffers = candidates?.some(
    (candidate) => candidate?.serviceOffer
  );

  const handelServiceRequestCancellation = () => {
    navigate(
      serviceRequestHasOffers
        ? "LEGACY_HAS_CONTRACT_TASK_CANCEL"
        : "LEGACY_NO_CONTRACT_TASK_CANCEL",
      {
        reload: true,
        queryParams: { id: String(serviceRequest.id) },
      }
    );
  };

  return (
    <VStack
      w="full"
      alignItems="start"
      spacing="18px"
      background="white"
      p={{ base: "16px", lg: "24px" }}
      borderRadius="10px"
    >
      <HStack w="full" spacing="8px" justifyContent="space-between">
        <HStack
          spacing="6px"
          px="10px"
          py="4px"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="full"
        >
          {serviceRequest?.pCategoryId > 1 &&
            serviceRequest?.pCategoryId < 12 && (
              <Box w="16px" h="16px" flexShrink={0}>
                <Image
                  src={require(`assets/images/cat/svg/s_cat/${serviceRequest.pCategoryId}.svg`)}
                  w="16px"
                  h="16px"
                  alt=""
                />
              </Box>
            )}
          {messages[`s_cat.${serviceRequest?.sCategory}`] && (
            <Text fontSize="sm" color="gray.700" m="0">
              <FormattedMessage
                {...messages[`s_cat.${serviceRequest.sCategory}`]}
              />
            </Text>
          )}
        </HStack>
        <Menu>
          <MenuButton
            as={IconButton}
            variant="secondary"
            size="sm"
            alignSelf="start"
            aria-label={messages["actions"].defaultMessage}
            icon={<Icon as={Cog} width="14px" height="14px" />}
          />
          <MenuList minWidth="79px">
            <MenuItem
              onClick={() => {
                navigate("LEGACY_SERVICE_REQUEST_EDIT", {
                  reload: true,
                  params: { id: String(serviceRequest.id) },
                });
              }}
            >
              <FormattedMessage {...messages["edit"]} />
            </MenuItem>
            <MenuItem
              onClick={
                serviceRequest?.appliedServiceProvidersCount < 1
                  ? onCancelConfirmationOpen
                  : handelServiceRequestCancellation
              }
            >
              <FormattedMessage
                {...messages["serviceRequest.declineOffer.cancelButton.text"]}
              />
            </MenuItem>
          </MenuList>
        </Menu>
        <ConfirmationModal
          title={formatMessage(messages["serviceRequest.cancellation.title"])}
          message={formatMessage(
            messages["serviceRequest.cancellation.message"]
          )}
          actionText={formatMessage(
            messages["serviceRequest.cancellation.confirmCTAText"]
          )}
          cancelActionText={formatMessage(
            messages["serviceRequest.cancellation.cancelCTAText"]
          )}
          isOpen={isCancelConfirmationOpen}
          onClose={onCancelConfirmationClose}
          onAction={handelServiceRequestCancellation}
        />
      </HStack>

      <Tooltip label={serviceRequest.title}>
        <Heading
          fontSize={{ base: "2xl", md: "4xl" }}
          _firstLetter={{ textTransform: "uppercase" }}
          noOfLines={2}
        >
          {serviceRequest.title}
        </Heading>
      </Tooltip>

      <DetailLine
        title={formatMessage(messages["newTaskShow.address"])}
        detail={serviceRequest?.placeName}
        icon={
          <Icon as={LocationDot} width="16px" height="16px" color="gray.500" />
        }
        underlined
      />

      {serviceRequest?.recurrencyIntent === "frequent" &&
        serviceRequest?.authorFirstName && (
          <DetailLine
            title={formatMessage(messages["newTaskShow.recurrentJob"])}
            detail={formatMessage(messages["serviceRequest.frequentJob.body"], {
              spName: serviceRequest.authorFirstName,
            })}
            icon={
              <Image
                src={require("assets/images/recurrency/main-icon.svg")}
                w="16px"
                h="16px"
                alt="recurrent"
              />
            }
            underlined
          />
        )}

      <Accordion allowToggle border="none" w="full">
        <AccordionItem border="none">
          <AccordionPanel p="0" pb={4}>
            <VStack w="full" alignItems="start" spacing="18px">
              <VStack w="full" alignItems="start" spacing="8px">
                <Text
                  as="span"
                  m="0"
                  fontSize="xxs"
                  fontWeight="bold"
                  color="gray.900"
                  textTransform="uppercase"
                  letterSpacing="wide"
                >
                  {formatMessage(messages["newTaskShow.photos"])}
                </Text>
                {pictures.length === 0 ? (
                  <Text
                    m="0"
                    fontSize="md"
                    fontStyle="italic"
                    color="gray.700"
                  >
                    <FormattedMessage {...messages["newTaskShow.noPhotos"]} />
                  </Text>
                ) : (
                  <Grid
                    gridTemplateColumns={{
                      base: "repeat(auto-fill, 151px)",
                      md: "repeat(auto-fill, 170px)",
                      lg: "repeat(auto-fill, 190px)",
                    }}
                    width="full"
                    gap="8px"
                    justifyContent="center"
                  >
                    {pictures.map((picture, index) => (
                      <GridItem
                        key={index}
                        h={{ base: "151px", md: "170px", lg: "190px" }}
                        w="inherit"
                        borderRadius="10px"
                        overflow="hidden"
                      >
                        <AspectRatio ratio={3 / 3}>
                          <Image
                            w="inherit"
                            h="inherit"
                            src={picture?.normal}
                            alt=""
                          />
                        </AspectRatio>
                      </GridItem>
                    ))}
                  </Grid>
                )}
              </VStack>

              <Divider color="gray.300" />

              <DetailLine
                title={formatMessage(messages["newTaskShow.description"])}
                detail={serviceRequest?.description}
              />

              <Divider color="gray.300" />

              <DetailLine
                title={formatMessage(messages["newTaskShow.typeOfProvider"])}
                detail={formatMessage(
                  messages[
                    `serviceRequest.serviceProviderType.option${
                      serviceRequest?.proOnly ? "2" : "1"
                    }`
                  ]
                )}
              />

              <Divider color="gray.300" />

              <DetailLine
                title={formatMessage(
                  messages[
                    isPostedForClient
                      ? "newTaskShow.requestedFor"
                      : "newTaskShow.requestedBy"
                  ]
                )}
                detail={`${serviceRequest?.clientName || ""} · ${dateFromNow(
                  serviceRequest?.postedDate
                )}`}
              />
            </VStack>
          </AccordionPanel>
          <AccordionButton p="0" justifyContent="center">
            <Link
              h="fit-content"
              textDecoration="underline"
              color="pink.500"
              onClick={onToggle}
              _hover={{ color: "pink.600" }}
            >
              <Text m="0" mr="4px" display="inline">
                <FormattedMessage
                  {...messages[isOpen ? "hideDetails" : "seeDetails"]}
                />
              </Text>
              <Icon
                as={ChevronDown}
                width="16px"
                height="16px"
                transform={isOpen ? "rotate(180deg)" : undefined}
              />
            </Link>
          </AccordionButton>
        </AccordionItem>
      </Accordion>
    </VStack>
  );
};
